import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FlaskConical, Sparkles, Wallet, ShieldCheck, RefreshCw, ChevronLeft, Send, Plus } from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

/**
 * Lab (V3.18) — « Le Lab · Défis & Micro-projets »
 * ────────────────────────────────────────────────
 * - Talents éligibles : liste des défis et projets ouverts + candidature.
 * - Employeurs : formulaire de création (tab "Publier") + accès à /lab/manage.
 * - Admin : accès total.
 */
export default function Lab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEmployer = ["employeur", "entrepreneur"].includes(user?.role);
  const isTalent = ["talent", "freelance"].includes(user?.role);
  const isAdmin = ["admin", "super-admin"].includes(user?.role);

  const [tab, setTab] = useState("challenge");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [insertion, setInsertion] = useState(null);
  const [submittingId, setSubmittingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch insertion state for talents (drives eligibility banner).
      const insertionRes = isTalent
        ? await api.get("/v1/insertion/me").catch(() => ({ data: null }))
        : { data: null };
      setInsertion(insertionRes.data);

      // Talents non-eligible : short-circuit the items request to avoid a 403.
      if (isTalent && insertionRes.data && insertionRes.data.applicable && !insertionRes.data.eligible) {
        setItems([]);
        return;
      }
      const it = await api.get(`/v1/lab/items?kind=${tab}`);
      setItems(it.data.items || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        setItems([]);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [tab, isTalent]);

  useEffect(() => { load(); }, [load]);

  const submit = async (itemId) => {
    setSubmittingId(itemId);
    try {
      await api.post(`/v1/lab/items/${itemId}/submit`, {});
      toast.success("Candidature envoyée !");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmittingId(null);
    }
  };

  // Talent non-éligible : bloquer avec un message contextuel
  const eligibilityBlocked = isTalent && insertion && insertion.applicable && !insertion.eligible;

  return (
    <PrivateLayout
      title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <FlaskConical size={20} /> Le Lab · Défis & Micro-projets
      </span>}
      breadcrumbs={[{ label: "Le Lab" }]}
      actions={
        <div style={{ display: "flex", gap: 6 }}>
          <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={load} data-testid="lab-refresh">
            <RefreshCw size={12} /> Actualiser
          </button>
          {(isEmployer || isAdmin) && (
            <>
              <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={() => setShowCreate((s) => !s)}
                      data-testid="lab-create-toggle">
                <Plus size={12} /> Publier
              </button>
              <Link to="/lab/manage" className="afr-btn afr-btn--outline afr-btn--sm">
                Gérer
              </Link>
            </>
          )}
        </div>
      }
    >
      <div data-testid="lab-page">
        <section
          className="afr-card"
          style={{ padding: 16, marginBottom: 16, background: "linear-gradient(135deg,#0F2A42 0%,#009640 100%)", color: "#fff" }}
          data-testid="lab-hero"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <FlaskConical size={30} />
            <div>
              <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>Brisez votre inactivité — démontrez vos compétences</div>
              <div style={{ fontSize: "0.88rem", marginTop: 4, opacity: 0.9 }}>
                Le Lab est votre passerelle vers l'expérience professionnelle. Choisissez un défi rapide (non rémunéré, pour la démonstration)
                ou candidatez à un premier projet pro rémunéré via séquestre AFRICTAL.
              </div>
            </div>
          </div>
        </section>

        {eligibilityBlocked && (
          <div className="afr-card" data-testid="lab-not-eligible-banner"
               style={{ padding: 14, background: "#FEF3C7", color: "#78350F", marginBottom: 12, borderLeft: "4px solid #B45309" }}>
            <strong>Le Lab est réservé au Parcours d'Insertion.</strong> Rendez-vous sur votre <Link to="/profile">profil</Link> et
            sélectionnez « Étudiant », « Jeune diplômé » ou « Diplômé sans expérience » pour y accéder.
          </div>
        )}

        {showCreate && (isEmployer || isAdmin) && (
          <CreateLabItem onCreated={() => { setShowCreate(false); load(); }} defaultKind={tab} />
        )}

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }} data-testid="lab-tabs">
          <button
            className={`afr-btn afr-btn--sm ${tab === "challenge" ? "afr-btn--primary" : "afr-btn--outline"}`}
            onClick={() => setTab("challenge")}
            data-testid="lab-tab-challenge"
          >
            <Sparkles size={12} /> Défis d'Évaluation
          </button>
          <button
            className={`afr-btn afr-btn--sm ${tab === "project" ? "afr-btn--primary" : "afr-btn--outline"}`}
            onClick={() => setTab("project")}
            data-testid="lab-tab-project"
          >
            <Wallet size={12} /> Premiers Projets Pro
          </button>
        </div>

        {/* List */}
        <section className="afr-card" style={{ padding: 12 }} data-testid="lab-list">
          {loading ? (
            <div>Chargement…</div>
          ) : items.length === 0 ? (
            <div className="afr-empty" data-testid="lab-empty">
              <div className="afr-empty__title">
                {tab === "challenge" ? "Aucun défi ouvert pour le moment" : "Aucun projet ouvert pour le moment"}
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {items.map((it) => (
                <article key={it.id} className="afr-card" style={{ padding: 12 }}
                         data-testid={`lab-item-${it.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{
                          padding: "2px 8px", borderRadius: 999, fontSize: "0.7rem", fontWeight: 700,
                          background: it.kind === "challenge" ? "#FEF3C7" : "#DCFCE7",
                          color: it.kind === "challenge" ? "#78350F" : "#065F46",
                        }}>
                          {it.kind === "challenge" ? "Défi (non rémunéré)" : `Premier Projet Pro · ${it.reward_amount.toLocaleString()} ${it.currency}`}
                        </span>
                        <span style={{
                          padding: "2px 8px", borderRadius: 4, fontSize: "0.7rem", fontWeight: 600,
                          background: "#F1F5F9", color: "#334155",
                        }} data-testid={`lab-item-skill-${it.id}`}>
                          Compétence : {it.skill_focus}
                        </span>
                      </div>
                      <h3 style={{ margin: "6px 0 4px", fontSize: "1rem" }}>{it.title}</h3>
                      <p style={{ margin: 0, fontSize: "0.85rem", color: "#475569", whiteSpace: "pre-wrap" }}>
                        {it.description}
                      </p>
                      <div style={{ marginTop: 6, fontSize: "0.72rem", color: "#94a3b8" }}>
                        {it.employer_name && <>Publié par <strong>{it.employer_name}</strong> · </>}
                        {it.duration_days ? `Durée estimée : ${it.duration_days} j · ` : ""}
                        {new Date(it.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    {isTalent && !eligibilityBlocked && (
                      <button
                        className="afr-btn afr-btn--primary afr-btn--sm"
                        onClick={() => submit(it.id)}
                        disabled={submittingId === it.id}
                        data-testid={`lab-submit-${it.id}`}
                      >
                        <Send size={12} /> Postuler
                      </button>
                    )}
                    {it.kind === "project" && (
                      <ShieldCheck size={14} title="Paiement sécurisé par escrow AFRICTAL"
                                   style={{ color: "#009640", flexShrink: 0 }} />
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </PrivateLayout>
  );
}

function CreateLabItem({ onCreated, defaultKind }) {
  const [kind, setKind] = useState(defaultKind || "challenge");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [skillFocus, setSkillFocus] = useState("");
  const [reward, setReward] = useState("");
  const [duration, setDuration] = useState("");
  const [audience, setAudience] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggleAudience = (v) => setAudience((a) => a.includes(v) ? a.filter((x) => x !== v) : [...a, v]);

  const submit = async () => {
    if (!title.trim() || !description.trim() || !skillFocus.trim()) {
      return toast.error("Titre, description et compétence obligatoires");
    }
    if (kind === "project" && (!reward || Number(reward) <= 0)) {
      return toast.error("Le Premier Projet Pro nécessite un budget > 0");
    }
    setBusy(true);
    try {
      const payload = {
        kind, title: title.trim(), description: description.trim(),
        skill_focus: skillFocus.trim(),
      };
      if (duration) payload.duration_days = Number(duration);
      if (kind === "project") payload.reward_amount = Number(reward);
      if (audience.length) payload.target_audience = audience;
      await api.post("/v1/lab/items", payload);
      toast.success("Publié dans Le Lab");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="afr-card" style={{ padding: 16, marginBottom: 14, background: "#F8FAFC" }}
             data-testid="lab-create-form">
      <h3 style={{ margin: "0 0 10px" }}>Publier dans Le Lab</h3>
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        {[
          { v: "challenge", label: "Défi d'Évaluation (non rémunéré)" },
          { v: "project", label: "Premier Projet Pro (rémunéré · escrow)" },
        ].map((k) => (
          <button
            key={k.v}
            className={`afr-btn afr-btn--sm ${kind === k.v ? "afr-btn--primary" : "afr-btn--outline"}`}
            onClick={() => setKind(k.v)}
            data-testid={`lab-create-kind-${k.v}`}
          >
            {k.label}
          </button>
        ))}
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        <input placeholder="Titre" value={title} onChange={(e) => setTitle(e.target.value)}
               data-testid="lab-create-title" style={input} />
        <textarea placeholder="Description (contexte, livrable attendu, critères…)"
                  rows={4} value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="lab-create-description"
                  style={{ ...input, resize: "vertical" }} />
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 8 }}>
          <input placeholder="Compétence ciblée (ex : React, Python)"
                 value={skillFocus} onChange={(e) => setSkillFocus(e.target.value)}
                 data-testid="lab-create-skill" style={input} />
          <input type="number" placeholder="Durée (jours)"
                 value={duration} onChange={(e) => setDuration(e.target.value)}
                 data-testid="lab-create-duration" style={input} />
          {kind === "project" && (
            <input type="number" placeholder="Budget XOF"
                   value={reward} onChange={(e) => setReward(e.target.value)}
                   data-testid="lab-create-reward" style={input} />
          )}
        </div>
        <div>
          <label style={{ fontSize: "0.8rem", color: "#475569", fontWeight: 600 }}>Cible (V3.20 — optionnel)</label>
          <div style={{ display: "flex", gap: 6, marginTop: 4 }} data-testid="lab-create-audience">
            {[
              { v: "etudiants", label: "Étudiants" },
              { v: "juniors", label: "Juniors" },
              { v: "jeunes_diplomes", label: "Jeunes diplômés" },
            ].map((a) => (
              <button
                key={a.v}
                type="button"
                className={`afr-btn afr-btn--sm ${audience.includes(a.v) ? "afr-btn--primary" : "afr-btn--outline"}`}
                onClick={() => toggleAudience(a.v)}
                data-testid={`lab-create-audience-${a.v}`}
              >
                {a.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: 4 }}>
            Sans sélection : visible à tous les profils du Parcours d'Insertion.
          </div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
        <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={submit} disabled={busy}
                data-testid="lab-create-submit">
          {busy ? "Publication…" : "Publier"}
        </button>
      </div>
    </section>
  );
}

const input = { padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: "0.9rem", width: "100%" };
